import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { PlanType, SubscriptionStatus, Usage } from '../types/subscription';
import { getPlan } from '../config/plans';
import { useUserStore } from './userStore';

interface SubscriptionStore {
  subscription: SubscriptionStatus;
  usage: Usage;
  updateSubscription: (subscription: Partial<SubscriptionStatus>) => void;
  incrementUsage: (type: keyof Usage) => void;
  resetUsage: () => void;
  canUseFeature: (feature: keyof Usage) => boolean;
}

export const useSubscriptionStore = create<SubscriptionStore>()(
  persist(
    (set, get) => ({
      subscription: {
        planId: 'free',
        isActive: true,
        cancelAtPeriodEnd: false
      },
      usage: {
        contentIdeasUsed: 0,
        contentPlansUsed: 0,
        platformsConnected: 0
      },
      updateSubscription: (newSubscription) => {
        console.log('Updating subscription:', newSubscription);
        set((state) => ({
          subscription: { ...state.subscription, ...newSubscription }
        }));
      },
      incrementUsage: (type) => {
        // Skip usage tracking for admin users
        if (useUserStore.getState().isAdmin()) return;

        const currentPlan = getPlan(get().subscription.planId);
        if (!currentPlan) return;

        const currentUsage = get().usage[type];
        const limit = currentPlan.limits[type];

        if (currentUsage < limit) {
          set((state) => ({
            usage: {
              ...state.usage,
              [type]: currentUsage + 1
            }
          }));
        }
      },
      resetUsage: () => 
        set({ 
          usage: {
            contentIdeasUsed: 0,
            contentPlansUsed: 0,
            platformsConnected: 0
          }
        }),
      canUseFeature: (feature) => {
        // Admin users can always use features
        if (useUserStore.getState().isAdmin()) return true;

        const { subscription, usage } = get();
        const plan = getPlan(subscription.planId);
        if (!plan) return false;

        return usage[feature] < plan.limits[feature];
      }
    }),
    {
      name: 'subscription-storage',
      version: 1,
      migrate: (persistedState: any, version: number) => {
        if (version === 0) {
          return {
            subscription: {
              planId: 'free',
              isActive: true,
              cancelAtPeriodEnd: false
            },
            usage: {
              contentIdeasUsed: 0,
              contentPlansUsed: 0,
              platformsConnected: 0
            }
          };
        }
        return persistedState;
      }
    }
  )
);